import { EWSTypeCurrency, sumOrError } from './ews';
import { isNumeric } from './market-development';

const sfpNumColumn = 2;
const readyOnlys = [0, 24, 25, 26, 36, 37, 38, 54, 55, 56];
const sumOnlys = [24, 36, 54, 56];

function cleanSFPCells(labels) {
	const cells = labels?.map((label, i) => {
		if (readyOnlys?.includes(i)) {
			if (sumOnlys?.includes(i)) {
				return [
					{ value: '0', readOnly: true },
					{ value: '0', readOnly: true },
				];
			} else {
				return [
					{ value: '', readOnly: true },
					{ value: '', readOnly: true },
				];
			}
		} else {
			return [
				{ value: '', readOnly: false },
				{ value: '', readOnly: false },
			];
		}
	});
	return cells;
}

function detailSFPCells({ code = null, labels = [], detailLabels = [], readOnly = false }) {
	const localCells = labels?.map((label, i) => {
		if (readyOnlys?.includes(i)) {
			if (sumOnlys?.includes(i)) {
				return [
					{ value: '0', readOnly: true },
					{ value: '0', readOnly: true },
				];
			} else {
				return [
					{ value: '', readOnly: true },
					{ value: '', readOnly: true },
				];
			}
		} else {
			const detailLabel = detailLabels?.find((el) => el?.templateLabel?.code === label?.code);
			if (detailLabel) {
				return [
					{ value: detailLabel?.values?.find((value) => value?.currency === EWSTypeCurrency.USD)?.value || 0, readOnly },
					{ value: detailLabel?.values?.find((value) => value?.currency === EWSTypeCurrency.KHR)?.value || 0, readOnly },
				];
			} else {
				return [
					{ value: '', readOnly },
					{ value: '', readOnly },
				];
			}
		}
	});

	const { cells } = calculateSFPCells({ code, cells: localCells, numLabel: labels?.length, readOnly });

	return cells;
}

function detailSFPRatioCells({ code = null, labels = [], detailLabels = [], readOnly = false }) {
	const TOTAL_COLS = 3;

	const normalize = (s) => (s || '').trim().toUpperCase();
	const makeCells = (val, ro) => Array.from({ length: TOTAL_COLS }, () => ({ value: val, readOnly: ro }));
	const toCell = (v, ro = readOnly) => ({ value: v ?? '', readOnly: ro });

	const localCells = labels?.map((label, i) => {
		if (readyOnlys?.includes(i)) {
			const fill = sumOnlys?.includes(i) ? '0' : '';
			return makeCells(fill, true);
		}

		const detailLabel = detailLabels?.find((el) => normalize(el?.templateLabel?.code) === normalize(label?.code));

		if (!detailLabel) {
			return makeCells('', readOnly);
		}

		const vals = detailLabel.values || [];

		if (vals.length >= TOTAL_COLS) {
			return [toCell(vals[0]?.value), toCell(vals[1]?.value), toCell(vals[2]?.value)];
		}

		const usd = vals.find((v) => v?.currency === EWSTypeCurrency.USD)?.value ?? '';

		return [toCell(usd), toCell(''), toCell('')];
	});

	const { cells } = calculateSFPRatioCells({
		code,
		cells: localCells,
		numLabel: labels?.length,
		readOnly,
	});

	return cells;
}

function calculateSFPCells({ code, cells, numLabel, readOnly = false }) {
	// Total assets
	let taSums = Array.from({ length: 23 }, (item, i) => i + 1);
	let taUSD = 0;
	let taKHR = 0;

	// Total equity
	let teSums = Array.from({ length: 9 }, (item, i) => i + 27);
	let teUSD = 0;
	let teKHR = 0;

	// Total liabilities
	let tlSums = Array.from({ length: 15 }, (item, i) => i + 39);
	let tlUSD = 0;
	let tlKHR = 0;

	let localCells = cells?.map((cols, i) => {
		if (readyOnlys?.includes(i)) {
			if (i === 24) {
				return [
					{ value: sumOrError([taUSD]), readOnly: true },
					{ value: sumOrError([taKHR]), readOnly: true },
				];
			} else if (i === 36) {
				return [
					{ value: sumOrError([teUSD]), readOnly: true },
					{ value: sumOrError([teKHR]), readOnly: true },
				];
			} else if (i === 54) {
				return [
					{ value: sumOrError([tlUSD]), readOnly: true },
					{ value: sumOrError([tlKHR]), readOnly: true },
				];
			} else if (i === 56) {
				return [
					{
						value: sumOrError([teUSD, tlUSD]),
						readOnly: true,
					},
					{
						value: sumOrError([teKHR, tlKHR]),
						readOnly: true,
					},
				];
			} else {
				return [
					{ value: '', readOnly: true },
					{ value: '', readOnly: true },
				];
			}
		} else {
			if (taSums?.includes(i)) {
				taUSD = taUSD + parseFloat(cols[0]?.value || 0);
				taKHR = taKHR + parseFloat(cols[1]?.value || 0);
			} else if (teSums?.includes(i)) {
				teUSD = teUSD + parseFloat(cols[0]?.value || 0);
				teKHR = teKHR + parseFloat(cols[1]?.value || 0);
			} else if (tlSums?.includes(i)) {
				tlUSD = tlUSD + parseFloat(cols[0]?.value || 0);
				tlKHR = tlKHR + parseFloat(cols[1]?.value || 0);
			}
			return [
				{ value: cols[0]?.value, readOnly },
				{ value: cols[1]?.value, readOnly },
			];
		}
	});
	localCells = localCells.slice(0, numLabel);
	return { code, cells: localCells };
}

function calculateSFPRatioCells({ code, cells, numLabel, readOnly = false }) {
	const NUM_YEARS = 3; // CY, PY, P2Y
	const TOTAL_COLS = NUM_YEARS; // 1 column per year (USD)

	// Row indices for summing
	const taRows = Array.from({ length: 23 }, (_, i) => i + 1); // Total Assets rows
	const teRows = Array.from({ length: 9 }, (_, i) => i + 27); // Total Equity rows
	const tlRows = Array.from({ length: 15 }, (_, i) => i + 39); // Total Liabilities rows

	// Running sums for each year
	const taUSD = Array(NUM_YEARS).fill(0);
	const teUSD = Array(NUM_YEARS).fill(0);
	const tlUSD = Array(NUM_YEARS).fill(0);

	// Normalize a row to have NUM_YEARS columns
	const normalizeRow = (row = []) => {
		const r = [...row];
		for (let c = 0; c < TOTAL_COLS; c++) {
			r[c] = r[c] ?? { value: '0' };
		}
		return r;
	};

	let localCells = (cells || []).map((row, i) => {
		const r = normalizeRow(row);

		if (readyOnlys.includes(i)) {
			// SUM ROWS
			if (i === 24) return taUSD.map((v) => ({ value: sumOrError([v]), readOnly: true })); // Total Assets
			if (i === 36) return teUSD.map((v) => ({ value: sumOrError([v]), readOnly: true })); // Total Equity
			if (i === 54) return tlUSD.map((v) => ({ value: sumOrError([v]), readOnly: true })); // Total Liabilities
			if (i === 56) return taUSD.map((v, idx) => ({ value: sumOrError([teUSD[idx], tlUSD[idx]]), readOnly: true })); // Equity + Liabilities
			// Other readOnly rows
			return Array(NUM_YEARS).fill({ value: '', readOnly: true });
		}

		// Accumulate sums for non-readOnly rows
		for (let yearIdx = 0; yearIdx < NUM_YEARS; yearIdx++) {
			const colValue = parseFloat(r[yearIdx]?.value || 0);
			if (taRows.includes(i)) taUSD[yearIdx] += colValue;
			if (teRows.includes(i)) teUSD[yearIdx] += colValue;
			if (tlRows.includes(i)) tlUSD[yearIdx] += colValue;
		}

		// Return cells for this row
		return r.slice(0, TOTAL_COLS).map((c) => ({ value: c.value, readOnly }));
	});

	localCells = localCells.slice(0, numLabel); // trim extra rows if any
	return { code, cells: localCells };
}

function validateSFPCells({ cells }) {
	const localCells = cells?.reduce((iPre, iCur) => {
		let localIPre = iPre;
		localIPre.push(
			...iCur?.reduce((jPre, jCur) => {
				let localJPre = jPre;
				if (!jCur?.readOnly && !isNumeric(jCur?.value)) {
					localJPre.push(jCur);
				}
				return localJPre;
			}, [])
		);
		return localIPre;
	}, []);
	return localCells?.length === 0;
}

export { sfpNumColumn, cleanSFPCells, detailSFPCells, detailSFPRatioCells, calculateSFPCells, calculateSFPRatioCells, validateSFPCells };
